import { useRef, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Group,
  Checkbox,
  Modal,
  NumberInput,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Textarea,
  Title,
} from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';
import { useDebouncedValue } from '@mantine/hooks';
import {
  bankImportControllerPruefen,
  getBankImportControllerListeQueryKey,
  useBankImportControllerEinlesen,
  useBankImportControllerIgnorieren,
  useBankImportControllerListe,
  useBankImportControllerRechnungen,
  useBankImportControllerVorschlaege,
  useBankImportControllerZuordnen,
  useBankImportControllerZuruecksetzen,
  type ApiError,
} from '@werkbank/shared';
import { TablePager, useSeiten } from '../components/TablePager';
import { PageHeader } from '../components/PageHeader';
import { TableCard } from '../components/TableCard';
import { formatCents, formatDateDE } from '../lib/format';
import { useGemerkt } from '../lib/gemerkt';

type Pruefung = Awaited<ReturnType<typeof bankImportControllerPruefen>>;
type Vorschlag = {
  id: string;
  number: string;
  legacyNummer: string | null;
  kunde: string;
  offenCents: number;
  skontoCents: number;
};
type Posten = { rechnung: Vorschlag; betragCents: number; skontoCents: number };
type Eingang = {
  id: string;
  bank: string;
  bookedOn: string;
  amountCents: number;
  counterparty: string;
  purpose: string;
  status: string;
  note: string | null;
  gebuchtAuf: { paymentId: string; amountCents: number; number: string | null }[];
};

/** Datei-Bytes → base64 (der Server erkennt Sparkasse und Commerzbank selbst). */
async function fileToBase64(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin);
}

const ART_TEXT: Record<string, string> = {
  EINDEUTIG: 'wird gebucht',
  EINDEUTIG_SKONTO: 'wird mit Skonto gebucht',
  KEINE_NUMMER: 'keine Belegnummer',
  UNBEKANNTE_NUMMER: 'Nummer unbekannt',
  MEHRERE_RECHNUNGEN: 'mehrere Rechnungen',
  BETRAG_ZU_NIEDRIG: 'Betrag zu niedrig',
  BETRAG_ZU_HOCH: 'Betrag zu hoch',
};

/**
 * Zahlungseingänge (Faruq 2026-08-29): Kontoauszug von Sparkasse oder
 * Commerzbank einlesen. Wo genau eine offene Rechnung gemeint ist und der
 * Betrag aufgeht — centgenau oder abzüglich des auf der Rechnung ausgewiesenen
 * Skontos —, wird automatisch gebucht. Alles andere bleibt als Prüffall stehen;
 * darüber entscheidet das Büro.
 *
 * Der Prüflauf ist bewusst vorgeschaltet und schreibt nichts: bei Geld soll man
 * sehen, was geschieht, bevor es geschieht.
 */
export function ZahlungseingaengePage() {
  const qc = useQueryClient();
  const dateiRef = useRef<HTMLInputElement>(null);
  const [pruefung, setPruefung] = useState<Pruefung | null>(null);
  const [datei, setDatei] = useState<{ name: string; base64: string } | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [alle, setAlle] = useGemerkt('zahlungseingaenge.alle', false);
  const zuruecksetzen = useBankImportControllerZuruecksetzen();
  const [fall, setFall] = useState<Eingang | null>(null);

  const liste = useBankImportControllerListe<Eingang[], ApiError>({
    alle: alle ? 'true' : 'false',
  });
  const einlesen = useBankImportControllerEinlesen();

  const auffrischen = () =>
    void qc.invalidateQueries({ queryKey: getBankImportControllerListeQueryKey() });

  async function dateiGewaehlt(f: File | undefined) {
    if (!f) return;
    setFehler(null);
    setPruefung(null);
    setLaeuft(true);
    try {
      const base64 = await fileToBase64(f);
      setDatei({ name: f.name, base64 });
      setPruefung(await bankImportControllerPruefen({ contentBase64: base64, filename: f.name }));
    } catch (e) {
      setFehler((e as ApiError).message ?? 'Die Datei konnte nicht gelesen werden.');
      setDatei(null);
    } finally {
      setLaeuft(false);
      if (dateiRef.current) dateiRef.current.value = '';
    }
  }

  function uebernehmen() {
    if (!datei) return;
    setFehler(null);
    einlesen.mutate(
      { data: { contentBase64: datei.base64, filename: datei.name } },
      {
        onSuccess: () => {
          setPruefung(null);
          setDatei(null);
          auffrischen();
        },
        onError: (e) => setFehler((e as ApiError).message),
      },
    );
  }

  const offene = liste.data ?? [];
  const seiten = useSeiten(offene, 'zahlungseingaenge');

  return (
    <Stack gap="lg">
      <PageHeader
        title="Zahlungseingänge"
        meta={
          liste.data ? `${offene.filter((z) => z.status === 'OPEN').length} zu prüfen` : 'lädt…'
        }
        action={
          <>
            <input
              ref={dateiRef}
              type="file"
              accept=".csv,text/csv"
              style={{ display: 'none' }}
              onChange={(e) => void dateiGewaehlt(e.target.files?.[0])}
              data-testid="bank-datei"
            />
            <Button onClick={() => dateiRef.current?.click()} loading={laeuft}>
              Kontoauszug wählen
            </Button>
          </>
        }
      />

      {fehler && <Alert color="red">{fehler}</Alert>}

      {pruefung && datei && (
        <TableCard
          testId="bank-pruefung"
          title={
            <Group justify="space-between" align="baseline" w="100%">
              <span>
                Prüflauf {datei.name} · {pruefung.bank}
              </span>
              <Group gap="xs">
                <Button
                  variant="subtle"
                  onClick={() => {
                    setPruefung(null);
                    setDatei(null);
                  }}
                >
                  Verwerfen
                </Button>
                <Button
                  onClick={uebernehmen}
                  loading={einlesen.isPending}
                  disabled={pruefung.eingaenge - pruefung.bereitsBekannt === 0}
                  data-testid="bank-uebernehmen"
                >
                  Übernehmen
                </Button>
              </Group>
            </Group>
          }
        >
          <Stack gap="sm" p="md">
            <Text size="sm">
              {pruefung.eingaenge} Zahlungseingänge in {pruefung.zeilen} Buchungen ·{' '}
              <b>{pruefung.automatisch}</b> werden automatisch gebucht · <b>{pruefung.pruefen}</b>{' '}
              zur Prüfung
              {pruefung.bereitsBekannt > 0 && (
                <> · {pruefung.bereitsBekannt} bereits eingelesen (werden übersprungen)</>
              )}
              {(pruefung.doppelt ?? 0) > 0 && (
                <>
                  {' '}
                  · {pruefung.doppelt} schon aus einem anderen Export vorhanden (werden
                  übersprungen)
                </>
              )}
            </Text>
            {pruefung.leseFehler.length > 0 && (
              <Alert color="yellow">
                {pruefung.leseFehler.length} Zeilen konnten nicht gelesen werden (
                {pruefung.leseFehler
                  .slice(0, 3)
                  .map((f) => `Zeile ${f.zeile}`)
                  .join(', ')}
                …). Sie werden nicht übernommen.
              </Alert>
            )}
          </Stack>
          <Table className="wb-orders-table">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Datum</Table.Th>
                <Table.Th ta="right">Betrag</Table.Th>
                <Table.Th className="wb-col-flex">Zahler / Verwendungszweck</Table.Th>
                <Table.Th>Ergebnis</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {pruefung.vorschau.map((z, i) => (
                <Table.Tr key={i}>
                  <Table.Td>{formatDateDE(z.gebuchtAm)}</Table.Td>
                  <Table.Td ta="right">{formatCents(z.betragCents)}</Table.Td>
                  <Table.Td className="wb-col-flex">
                    <Text size="sm" fw={500} truncate>
                      {z.gegenpartei}
                    </Text>
                    <Text size="xs" c="dimmed" truncate>
                      {z.zweck}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge
                      className="wb-status-badge"
                      color={z.art.startsWith('EINDEUTIG') ? 'green' : 'yellow'}
                      variant="light"
                    >
                      {ART_TEXT[z.art] ?? z.art}
                    </Badge>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </TableCard>
      )}

      <TableCard
        testId="bank-liste"
        footer={<TablePager p={seiten} einzahl="Eingang" mehrzahl="Eingänge" testId="bank-pager" />}
        title={
          <Group justify="space-between" align="center" w="100%">
            <span>{alle ? 'Alle Zahlungseingänge' : 'Zu prüfen'}</span>
            <Switch
              size="sm"
              label="Auch erledigte zeigen"
              checked={alle}
              onChange={(e) => setAlle(e.currentTarget.checked)}
            />
          </Group>
        }
      >
        <Table className="wb-orders-table">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Datum</Table.Th>
              <Table.Th ta="right">Betrag</Table.Th>
              <Table.Th className="wb-col-flex">Zahler / Verwendungszweck</Table.Th>
              <Table.Th>Stand</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {offene.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={5}>
                  <Text c="dimmed" size="sm" py="sm">
                    {alle
                      ? 'Noch nichts eingelesen.'
                      : 'Nichts zu prüfen — alle Eingänge sind zugeordnet.'}
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
            {seiten.sichtbar.map((z) => (
              <Table.Tr key={z.id}>
                <Table.Td>{formatDateDE(z.bookedOn)}</Table.Td>
                <Table.Td ta="right">{formatCents(z.amountCents)}</Table.Td>
                <Table.Td className="wb-col-flex">
                  <Text size="sm" fw={500} truncate>
                    {z.counterparty}
                  </Text>
                  <Text size="xs" c="dimmed" truncate>
                    {z.purpose}
                  </Text>
                  {z.note && (
                    <Text size="xs" c="dimmed">
                      {z.note}
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>
                  <Stand eingang={z} />
                </Table.Td>
                <Table.Td>
                  {z.status === 'OPEN' ? (
                    <Button size="xs" variant="light" onClick={() => setFall(z)}>
                      Zuordnen
                    </Button>
                  ) : (
                    // Falsch zugeordnet oder voreilig abgehakt: wieder oeffnen,
                    // dann neu zuordnen — das ist der Bearbeiten-Weg.
                    <Button
                      size="xs"
                      variant="subtle"
                      color="gray"
                      loading={zuruecksetzen.isPending && zuruecksetzen.variables?.id === z.id}
                      onClick={() => zuruecksetzen.mutate({ id: z.id }, { onSuccess: auffrischen })}
                      data-testid={`bank-reset-${z.id}`}
                    >
                      Zurücksetzen
                    </Button>
                  )}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </TableCard>

      {fall && <ZuordnenModal eingang={fall} onClose={() => setFall(null)} onDone={auffrischen} />}
    </Stack>
  );
}

function Stand({ eingang }: { eingang: Eingang }) {
  if (eingang.status === 'AUTO_BOOKED' || eingang.status === 'BOOKED') {
    const nummern = eingang.gebuchtAuf
      .map((g) => g.number)
      .filter(Boolean)
      .join(', ');
    return (
      <Badge className="wb-status-badge" color="green" variant="light">
        {eingang.status === 'AUTO_BOOKED' ? 'automatisch' : 'zugeordnet'}
        {nummern && ` · ${nummern}`}
      </Badge>
    );
  }
  if (eingang.status === 'IGNORED')
    return (
      <Badge className="wb-status-badge" color="gray" variant="light">
        kein Bezug
      </Badge>
    );
  return (
    <Badge className="wb-status-badge" color="yellow" variant="light">
      offen
    </Badge>
  );
}

/**
 * Prüffall entscheiden: einer ODER MEHREREN Rechnungen zuordnen, oder als
 * „kein Bezug" abhaken. Faruq 2026-09: manche Kunden überweisen eine Summe
 * für x Rechnungen — deshalb Suchfeld über Nummer, Sage-Nummer und Kunde,
 * Mehrfachauswahl mit Betrag je Rechnung und laufender Restsumme. Die
 * Vorschläge des Servers (Text- und Betragstreffer) stehen oben, solange
 * nichts gesucht wird.
 */
function ZuordnenModal({
  eingang,
  onClose,
  onDone,
}: {
  eingang: Eingang;
  onClose: () => void;
  onDone: () => void;
}) {
  const [suche, setSuche] = useState('');
  const [debounced] = useDebouncedValue(suche.trim(), 250);
  const [gewaehlt, setGewaehlt] = useState<Map<string, Posten>>(new Map());
  const [notiz, setNotiz] = useState('');
  const [fehler, setFehler] = useState<string | null>(null);
  const zuordnen = useBankImportControllerZuordnen();
  const ignorieren = useBankImportControllerIgnorieren();
  const vorschlaege = useBankImportControllerVorschlaege(eingang.id);
  const suchergebnis = useBankImportControllerRechnungen(
    { search: debounced },
    { query: { enabled: debounced.length >= 2 } },
  );

  const liste: Vorschlag[] =
    debounced.length >= 2 ? (suchergebnis.data ?? []) : (vorschlaege.data ?? []);
  const verteilt = [...gewaehlt.values()].reduce((s, p) => s + p.betragCents + p.skontoCents, 0);
  const rest = eingang.amountCents - verteilt;

  const umschalten = (v: Vorschlag) => {
    const naechste = new Map(gewaehlt);
    if (naechste.has(v.id)) naechste.delete(v.id);
    else {
      // Vorbelegung: so viel wie offen ist, höchstens den noch unverteilten Rest.
      const betrag = Math.max(0, Math.min(v.offenCents, rest));
      naechste.set(v.id, { rechnung: v, betragCents: betrag, skontoCents: 0 });
    }
    setGewaehlt(naechste);
  };
  const setzeBetrag = (id: string, feld: 'betragCents' | 'skontoCents', euro: number) => {
    const naechste = new Map(gewaehlt);
    const p = naechste.get(id);
    if (p) naechste.set(id, { ...p, [feld]: Math.round((Number(euro) || 0) * 100) });
    setGewaehlt(naechste);
  };
  const kannSpeichern =
    gewaehlt.size > 0 && rest >= 0 && [...gewaehlt.values()].every((p) => p.betragCents > 0);

  return (
    <Modal opened onClose={onClose} title="Zahlungseingang zuordnen" size="xl">
      <Stack gap="md">
        <div>
          <Text fw={600}>
            {formatCents(eingang.amountCents)} von {eingang.counterparty}
          </Text>
          <Text size="sm" c="dimmed">
            {eingang.purpose}
          </Text>
          {eingang.note && (
            <Text size="xs" c="dimmed" mt={4}>
              {eingang.note}
            </Text>
          )}
        </div>
        {fehler && <Alert color="red">{fehler}</Alert>}

        <TextInput
          label="Offene Rechnungen suchen"
          placeholder="Rechnungsnummer, Sage-Nummer oder Kunde…"
          value={suche}
          onChange={(e) => setSuche(e.currentTarget.value)}
          data-testid="bank-suche"
        />
        <Text size="xs" c="dimmed">
          {debounced.length >= 2
            ? `${liste.length} Treffer${liste.length === 50 ? ' (mehr über eine genauere Suche)' : ''}`
            : 'Vorschläge nach Verwendungszweck und Betrag — oder oben suchen.'}
        </Text>
        {liste.length === 0 && !vorschlaege.isLoading && !suchergebnis.isLoading && (
          <Text size="sm" c="dimmed">
            {debounced.length >= 2
              ? 'Keine offene Rechnung passt zur Suche.'
              : 'Keine passende offene Rechnung gefunden — Betrag und Verwendungszweck ergeben keinen Treffer.'}
          </Text>
        )}
        <Stack gap={4} mah={260} style={{ overflowY: 'auto' }}>
          {liste.map((v) => (
            <Group
              key={v.id}
              justify="space-between"
              p="xs"
              wrap="nowrap"
              style={{
                border: '1px solid var(--wb-border)',
                borderRadius: 6,
                background: gewaehlt.has(v.id) ? 'var(--wb-surface-hover)' : undefined,
                cursor: 'pointer',
              }}
              onClick={() => umschalten(v)}
              data-testid={`bank-rechnung-${v.number}`}
            >
              <Group gap="sm" wrap="nowrap">
                <Checkbox checked={gewaehlt.has(v.id)} readOnly tabIndex={-1} />
                <div>
                  <Text size="sm" fw={500} className="wb-tnum">
                    {v.number}
                    {v.legacyNummer ? ` · Sage ${v.legacyNummer}` : ''}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {v.kunde}
                  </Text>
                </div>
              </Group>
              <Text size="sm" c="dimmed" className="wb-tnum">
                offen {formatCents(v.offenCents)}
              </Text>
            </Group>
          ))}
        </Stack>

        {gewaehlt.size > 0 && (
          <Stack gap="xs">
            <Title order={6}>Aufteilung</Title>
            {[...gewaehlt.values()].map((p) => (
              <Group key={p.rechnung.id} grow align="flex-end">
                <Text size="sm" className="wb-tnum">
                  {p.rechnung.number} (offen {formatCents(p.rechnung.offenCents)})
                </Text>
                <NumberInput
                  label="Zahlbetrag (€)"
                  size="xs"
                  value={p.betragCents / 100}
                  onChange={(v) => setzeBetrag(p.rechnung.id, 'betragCents', Number(v))}
                  decimalScale={2}
                  fixedDecimalScale
                  min={0}
                />
                <NumberInput
                  label="davon Skonto (€)"
                  size="xs"
                  value={p.skontoCents / 100}
                  onChange={(v) => setzeBetrag(p.rechnung.id, 'skontoCents', Number(v))}
                  decimalScale={2}
                  fixedDecimalScale
                  min={0}
                />
              </Group>
            ))}
            <Text
              size="sm"
              c={rest < 0 ? 'red' : rest > 0 ? 'orange' : 'dimmed'}
              className="wb-tnum"
              data-testid="bank-rest"
            >
              {rest < 0
                ? `${formatCents(-rest)} zu viel verteilt.`
                : rest > 0
                  ? `${formatCents(rest)} bleiben unverteilt — der Eingang bleibt als Prüffall stehen.`
                  : 'Vollständig verteilt.'}
            </Text>
          </Stack>
        )}
        <Textarea
          label="Notiz"
          description="Bei überschrittener Skontofrist ist eine Begründung Pflicht."
          value={notiz}
          onChange={(e) => setNotiz(e.currentTarget.value)}
          autosize
          minRows={2}
        />

        <Group justify="space-between">
          <Button
            variant="subtle"
            color="gray"
            loading={ignorieren.isPending}
            onClick={() =>
              ignorieren.mutate(
                { id: eingang.id, data: { note: notiz } },
                {
                  onSuccess: () => {
                    onDone();
                    onClose();
                  },
                  onError: (e) => setFehler((e as ApiError).message),
                },
              )
            }
          >
            Kein Rechnungsbezug
          </Button>
          <Group gap="xs">
            <Button variant="subtle" onClick={onClose}>
              Abbrechen
            </Button>
            <Button
              disabled={!kannSpeichern}
              loading={zuordnen.isPending}
              data-testid="bank-zuordnen-speichern"
              onClick={() =>
                zuordnen.mutate(
                  {
                    id: eingang.id,
                    data: {
                      posten: [...gewaehlt.values()].map((p) => ({
                        documentId: p.rechnung.id,
                        amountCents: p.betragCents,
                        skontoCents: p.skontoCents,
                      })),
                      note: notiz || undefined,
                    },
                  },
                  {
                    onSuccess: () => {
                      onDone();
                      onClose();
                    },
                    onError: (e) => setFehler((e as ApiError).message),
                  },
                )
              }
            >
              {gewaehlt.size > 1 ? `${gewaehlt.size} Rechnungen zuordnen` : 'Zuordnen'}
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}
